<template>
  <PageContainer>
    <View gap="lg" direction="column">
      <View justify="space-between" align="center" :grow="1">
        <Text variant="h1">Showings</Text>

        <MenuButton :buttonLabel="selectedTimeFilter" size="medium">
          <MenuButtonItem
            v-for="filter in timeFilters"
            :key="filter"
            :title="filter"
            @click="onFilterSelected(filter)"
          />
        </MenuButton>
      </View>

      <View v-if="state.status === 'pending'" justify="center" padding="xl">
        <LoadingIndicator />
      </View>

      <EmptyPagePlaceholder
        v-else-if="state.status === 'error'"
        title="Could not load showings"
        description="Please reload the page or try again later"
      />

      <template v-else>
        <TopBarFilters :selectedFilter="selectedFilter" :filters="filters" @selected="filterShowings" />

        <EmptyPagePlaceholder
          v-if="showings.length === 0"
          title="You don't have any showings yet"
          description="Tallo is working to schedule showings. Once there is activity, you can see everything related to showings here"
        />

        <template v-else>
          <div class="showings">
            <ShowingCard v-for="showing in showings" :key="showing.id" :showing="showing" />
          </div>

          <div v-if="hasMore" :key="listVersion" ref="listEnd" data-test="showings-list-end" />

          <View v-if="isLoadingMore" justify="center" padding="md">
            <LoadingIndicator />
          </View>
        </template>
      </template>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';
import { computed, shallowRef, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  TopBarFilters,
  EmptyPagePlaceholder,
  PageContainer,
  Text,
  View,
  MenuButtonItem,
  MenuButton,
  LoadingIndicator,
} from '@tallo/design-system';
import { ShowingFilters, ShowingCard, useShowingsListQuery, ShowingStatus } from '@tallo/investor/showing';
import { TimeFilter, timeFilters } from '@tallo/utility';

const filters = Object.values(ShowingFilters);

const router = useRouter();
const route = useRoute();

const propertyId = computed(() => route.params.propertyId as string);
const selectedFilter = computed(() => route.query.filter as ShowingFilters);
const status = computed<ShowingStatus | undefined>(() =>
  selectedFilter.value === ShowingFilters.ALL ? undefined : (selectedFilter.value as unknown as ShowingStatus),
);
const selectedTimeFilter = computed({
  get: () => (route.query.timeFilter as TimeFilter) || TimeFilter.LAST_30_DAYS,
  set: (value: TimeFilter) => updateQueryFilters({ timeFilter: value }),
});

const { state, hasMore, isLoadingMore, loadMore } = useShowingsListQuery({
  propertyId,
  timeFilter: selectedTimeFilter,
  status,
});
const showings = computed(() => state.value.data?.showings || []);

// The next page loads as the end of the list nears. The end marker is rebuilt for every list that
// lands, so the observer reports afresh whether the end is still in reach (a tall screen) and only
// then asks for the page after it
const listEnd = useTemplateRef<HTMLElement>('listEnd');
const listVersion = shallowRef(0);

watch(
  () => state.value.data,
  () => listVersion.value++,
);

useIntersectionObserver(
  listEnd,
  ([entry]) => {
    if (entry?.isIntersecting) {
      loadMore();
    }
  },
  { rootMargin: '0px 0px 600px 0px' },
);

function onFilterSelected(filter: TimeFilter) {
  selectedTimeFilter.value = filter;
}

function filterShowings(filter: ShowingFilters) {
  updateQueryFilters({ filter });
}

function updateQueryFilters(params: { timeFilter?: TimeFilter; filter?: ShowingFilters }) {
  router.replace({
    query: {
      ...route.query,
      ...params,
    },
  });
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.showings {
  display: grid;
  gap: var(--t-spacing-md);
  grid-template-columns: repeat(auto-fill, minmax(23.5rem, 1fr));

  @include screen-max-sm {
    grid-template-columns: 1fr;
  }
}
</style>
